// Considered: rendering `DeclaredInheritedGroupMolecule` directly in the composer — rejected
// because the propsum framing (icon + count + "read here, changed on the aspect that holds
// them" head; the dashed, sunken border marking these rows as derived rather than authored,
// `trail.css:50-56` `.t-propsum`) is its own shape the group molecule doesn't carry. This
// molecule wraps it once so the framing exists in one place; the rows themselves are the
// SAME `DeclaredInheritedGroupMolecule` groups + `PropertySummaryRow` shape Own Properties used
// (spec Design System Reuse Audit — "Read-only merged property summary: reuse, re-framed").
import React from 'react';
import DeclaredInheritedGroupMolecule, { type RowGroup } from './DeclaredInheritedGroupMolecule';
import './MergedPropertySummaryMolecule.scss';

export interface MergedPropertySummaryMoleculeProps {
  /** "Properties a sphere of {name} carries" — composed by the caller, which owns `t()`. */
  headingText: string;
  /** Total row count across every group (`propRows.length`), not any one group's own count. */
  totalCount: number;
  /** "read here, changed on the aspect that holds them". */
  hint: string;
  /** The peer groups (from aspects / inherited / declared directly) — empty ones are not rendered. */
  groups: RowGroup[];
  /** Shown in place of the groups when `totalCount` is 0 — no aspect attached carries a property yet. */
  emptyMessage: string;
  emptyDatum?: string;
  headingDatum?: string;
  headingTextDatum?: string;
  countDatum?: string;
  hintDatum?: string;
  'data-testid'?: string;
}

const MergedPropertySummaryMolecule: React.FC<MergedPropertySummaryMoleculeProps> = ({
  headingText,
  totalCount,
  hint,
  groups,
  emptyMessage,
  emptyDatum,
  headingDatum,
  headingTextDatum,
  countDatum,
  hintDatum,
  'data-testid': dataTestId,
}) => (
  <div className="merged-property-summary" data-testid={dataTestId} data-container="compose > group">
    <div
      className="merged-property-summary__head"
      data-datum={headingDatum}
      data-kind={headingDatum ? 'heading' : undefined}
    >
      <i className="pi pi-list merged-property-summary__icon" aria-hidden="true" />
      <strong
        className="merged-property-summary__title"
        data-datum={headingTextDatum}
        data-kind={headingTextDatum ? 'strong' : undefined}
      >
        {headingText}
      </strong>
      <span className="merged-property-summary__count" data-datum={countDatum} data-kind={countDatum ? 'count' : undefined}>
        {totalCount}
      </span>
      <span className="merged-property-summary__hint" data-datum={hintDatum} data-kind={hintDatum ? 'hint' : undefined}>
        {hint}
      </span>
    </div>
    {totalCount === 0 ? (
      <p className="merged-property-summary__empty" data-datum={emptyDatum} data-kind="note">
        {emptyMessage}
      </p>
    ) : (
      <DeclaredInheritedGroupMolecule groups={groups} data-testid={dataTestId ? `${dataTestId}-groups` : undefined} />
    )}
  </div>
);

MergedPropertySummaryMolecule.displayName = 'MergedPropertySummaryMolecule';

export default MergedPropertySummaryMolecule;
